import { useEffect, useMemo, useState } from "react";
import type { TableFeedItem } from "../feedTypes.js";
import type { FeedCardComponentProps } from "../FeedRenderer.js";
import { formatTableValue, getUserLocale } from "../tableValueFormatter.js";

interface TableCardProps extends FeedCardComponentProps {
  item: TableFeedItem;
}

type TableRow = Record<string, unknown>;

type NormalizedData = {
  rows: TableRow[];
  totalCount: number;
};

function normalizeData(data: unknown): NormalizedData {
  if (Array.isArray(data)) {
    return { rows: data.filter((row): row is TableRow => typeof row === "object" && row !== null), totalCount: data.length };
  }

  if (data && typeof data === "object") {
    const typed = data as { items?: unknown[]; totalCount?: number };
    const rows = Array.isArray(typed.items)
      ? typed.items.filter((row): row is TableRow => typeof row === "object" && row !== null)
      : [];
    const totalCount = typeof typed.totalCount === "number" ? typed.totalCount : rows.length;
    return { rows, totalCount };
  }

  return { rows: [], totalCount: 0 };
}

function getAlignmentClass(align?: "left" | "center" | "right") {
  if (!align) return "";
  switch (align) {
    case "right":
      return " is-right";
    case "center":
      return " is-center";
    default:
      return "";
  }
}

export function TableCard({ item, data, loading, onAction }: TableCardProps) {
  const userLocale = useMemo(() => getUserLocale(), []);
  const { rows, totalCount } = useMemo(() => normalizeData(data), [data]);
  const pagination = item.config?.pagination;
  const [pageSize, setPageSize] = useState(() => {
    if (pagination?.pageSize && pagination.pageSize > 0) {
      return pagination.pageSize;
    }
    const fallback = Math.min(rows.length, 10);
    return fallback > 0 ? fallback : 10;
  });
  const [page, setPage] = useState(0);

  const totalPages = pagination ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;

  useEffect(() => {
    if (!pagination) return;
    setPage(0);
  }, [pagination, pageSize, totalCount]);

  useEffect(() => {
    if (!pagination) return;
    setPage((prev) => Math.min(prev, Math.max(totalPages - 1, 0)));
  }, [pagination, totalPages]);

  const paginatedRows = useMemo(() => {
    if (!pagination) return rows;
    const offset = page * pageSize;
    return rows.slice(offset, offset + pageSize);
  }, [pagination, rows, page, pageSize]);

  const handleRowClick = (row: TableRow) => {
    if (!item.config?.rowClickAction) return;
    onAction(item.config.rowClickAction, { itemId: item.id, source: "row", row, data });
  };

  const emptyState = item.config?.emptyState;
  const hasRows = paginatedRows.length > 0;
  const triggerEmptyStateAction = () => {
    if (!emptyState?.action) return;
    onAction(emptyState.action, { itemId: item.id, source: "inline" });
  };

  const pageSizeOptions = useMemo(() => {
    if (!pagination) return [] as number[];
    const base = pagination.pageSizeOptions && pagination.pageSizeOptions.length > 0
      ? pagination.pageSizeOptions
      : [pagination.pageSize, 10, 25, 50];
    return base
      .filter((option): option is number => typeof option === "number" && option > 0 && !Number.isNaN(option))
      .filter((option, index, arr) => arr.indexOf(option) === index)
      .sort((a, b) => a - b);
  }, [pagination]);

  return (
    <div className="feed-card-table">
      <div className="feed-card-table-container">
        <table>
          <thead>
            <tr>
              {item.config.columns.map((column) => (
                <th key={column.id} style={{ width: column.width }} className={getAlignmentClass(column.align)}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && !hasRows && emptyState ? (
              <tr className="feed-card-table-empty">
                <td colSpan={item.config.columns.length}>
                  <div className="feed-card-empty-state">
                    <h4>{emptyState.title}</h4>
                    {emptyState.description && <p>{emptyState.description}</p>}
                    {emptyState.action && (
                      <button
                        type="button"
                        className="feed-card-empty-action"
                        onClick={triggerEmptyStateAction}
                      >
                        {emptyState.action.label ?? "Aktion"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, index) => {
                const isInteractive = Boolean(item.config.rowClickAction);
                const key = (row.id ?? row._id ?? index) as string | number;
                return (
                  <tr
                    key={String(key)}
                    className={isInteractive ? "is-clickable" : undefined}
                    onClick={() => (isInteractive ? handleRowClick(row) : undefined)}
                    onKeyDown={(event) => {
                      if (!isInteractive) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleRowClick(row);
                      }
                    }}
                    role={isInteractive ? "button" : undefined}
                    tabIndex={isInteractive ? 0 : undefined}
                  >
                    {item.config.columns.map((column) => {
                      const rawValue = row[column.field];
                      const formatted = formatTableValue(rawValue, {
                        format: column.format,
                        valueMap: column.valueMap,
                        locale: userLocale
                      });
                      const pillClass =
                        column.format === "pill" || column.format === "status"
                          ? `feed-card-table-pill feed-card-table-pill--${String(rawValue ?? "default")
                              .toLowerCase()
                              .replace(/[^a-z0-9-]+/g, "-")}`
                          : undefined;
                      return (
                        <td key={column.id} className={getAlignmentClass(column.align)}>
                          <span className={pillClass}>{formatted}</span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && hasRows && totalPages > 1 && (
        <div className="feed-card-table-pagination">
          <div className="feed-card-table-page-size">
            <label>
              Einträge pro Seite
              <select
                value={pageSize}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setPageSize(value);
                  setPage(0);
                }}
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="feed-card-table-pagination-controls">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
            >
              Zurück
            </button>
            <span>
              Seite {page + 1} von {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={page >= totalPages - 1}
            >
              Weiter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
